/**
 * Realtime transcription client — the browser side of the live Azure STT
 * fallback. One long-lived WebSocket per interview; sends per-question
 * control frames and continuous 16 kHz Int16 PCM as binary.
 *
 * Owns nothing React-related. The `useRealtimeTranscription` hook
 * provides the React wiring (mic-tap lifecycle, mount/unmount) and
 * delegates everything else to this class.
 *
 * Failure philosophy: this client must never throw into its caller.
 * Losing the live transcript is acceptable; breaking the interview is
 * not. Every interaction with the WebSocket is guarded.
 */

export interface RealtimeTranscriptionClientOptions {
  /**
   * Test seam — injects a fake WebSocket constructor. Defaults to the
   * global WebSocket in production.
   */
  WebSocketImpl?: typeof WebSocket;
  /**
   * Initial reconnect delay in milliseconds; doubles each consecutive
   * failure up to `reconnectMaxMs`.
   */
  reconnectBaseMs?: number;
  reconnectMaxMs?: number;
}

type SocketState = 'idle' | 'connecting' | 'open' | 'closed';

const QUESTION_START_TYPE = 'question_start';
const SENTINEL_END_INDEX = 999;

export class RealtimeTranscriptionClient {
  private readonly url: string;
  private readonly WS: typeof WebSocket;
  private readonly reconnectBaseMs: number;
  private readonly reconnectMaxMs: number;

  private socket: WebSocket | null = null;
  private state: SocketState = 'idle';
  private explicitlyClosed = false;

  /**
   * The most recent question_index we tried to signal. We replay this
   * value on every (re)connect — older question_start signals are
   * irrelevant by the time the socket comes back, since the candidate
   * is already on a later question.
   */
  private pendingQuestionIndex: number | null = null;

  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(url: string, opts: RealtimeTranscriptionClientOptions = {}) {
    this.url = url;
    this.WS =
      opts.WebSocketImpl ??
      (typeof WebSocket !== 'undefined' ? WebSocket : (null as never));
    this.reconnectBaseMs = opts.reconnectBaseMs ?? 1000;
    this.reconnectMaxMs = opts.reconnectMaxMs ?? 8000;
  }

  open(): void {
    if (this.state === 'connecting' || this.state === 'open') return;
    this.explicitlyClosed = false;
    this.connect();
  }

  close(): void {
    this.explicitlyClosed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close(1000);
      } catch {
        // ignore — we're tearing down anyway
      }
    }
    this.socket = null;
    this.state = 'closed';
  }

  signalQuestionStart(questionIndex: number): void {
    this.pendingQuestionIndex = questionIndex;
    this.flushPendingQuestionStart();
  }

  signalEndOfQuestions(): void {
    this.signalQuestionStart(SENTINEL_END_INDEX);
  }

  pushPcm(pcm16: Int16Array): void {
    if (this.state !== 'open' || !this.socket) return;
    try {
      // Copy into a fresh ArrayBuffer — the source view may belong to a
      // recycled AudioWorklet buffer that the WS layer should not pin.
      const copy = new Int16Array(pcm16);
      this.socket.send(copy.buffer);
    } catch {
      // swallow — the next 'close' event will trigger reconnect
    }
  }

  // -------------------------------------------------------------------
  // Internal
  // -------------------------------------------------------------------

  private connect(): void {
    if (!this.WS) {
      // No WebSocket available in this runtime — silently no-op.
      this.state = 'closed';
      return;
    }

    let ws: WebSocket;
    try {
      ws = new this.WS(this.url);
    } catch {
      // Bad URL or environment refusal: don't crash, schedule a retry
      // unless we've been explicitly closed.
      this.state = 'closed';
      this.scheduleReconnect();
      return;
    }

    this.socket = ws;
    this.state = 'connecting';

    ws.onopen = () => {
      this.state = 'open';
      this.reconnectAttempts = 0;
      this.flushPendingQuestionStart();
    };
    ws.onclose = (ev) => {
      this.state = 'closed';
      this.socket = null;
      if (this.explicitlyClosed) return;
      // Normal closure code 1000 implies a clean intentional shutdown
      // from either side — if we didn't initiate it, treat it as a
      // server-side stop and don't reconnect.
      if (ev.code === 1000) return;
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      // onerror is informational; the matching onclose will follow.
    };
  }

  private scheduleReconnect(): void {
    if (this.explicitlyClosed) return;
    const delay = Math.min(
      this.reconnectBaseMs * 2 ** this.reconnectAttempts,
      this.reconnectMaxMs,
    );
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private flushPendingQuestionStart(): void {
    if (this.state !== 'open' || !this.socket) return;
    if (this.pendingQuestionIndex === null) return;
    const payload = JSON.stringify({
      type: QUESTION_START_TYPE,
      question_index: this.pendingQuestionIndex,
    });
    try {
      this.socket.send(payload);
      // Keep pendingQuestionIndex set — we want to replay it after a
      // reconnect since the new server-side recognizer needs to know
      // which question is current. It will be overwritten on the next
      // signalQuestionStart() call.
    } catch {
      // swallow — reconnect path will retry
    }
  }
}
